import {ProofRequirement} from "@/shared/timrTypes";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import {
	AttachmentDownloadUrlResponse,
	AttachmentFileMetadata,
} from "@/shared/timr";
import {api} from "@/shared/api";

const template = `
		<attachment-gallery id="proofAttachmentGallery"></attachment-gallery>
		<div id="proofAttachmentGalleryInfoPanel" class="panel panel-flat info-panel mt-10">
			<div class="panel-body">
				<i class="far fa-info-circle info-panel-icon"></i>
				${messages["proofAttachmentGallery.proof.info"]}
			</div>
		</div>
	`;
	
	export class ProofAttachmentGallery extends HTMLElement {
		private static readonly EMPTY_ATTACHMENTS: ProofAttachmentGalleryAttachment[] = [];
		private static readonly PROOF_ATTACHMENT_ACCEPT_STRING = "image/*,application/pdf";
		private static readonly MAX_PROOF_ATTACHMENT_COUNT = 5;
		private static readonly MAX_PROOF_ATTACHMENT_SIZE = 20_000_000; // 20MB

		public static readonly EVENT_FILES_CHANGED = AttachmentGallery.EVENT_FILES_CHANGED;

		private proofRequirement: ProofRequirement = ProofRequirement.NOT_APPLICABLE;

		private attachmentGallery: AttachmentGallery;
		private loadAttachmentsUri: string | null;

		public setReadonly(readonly: boolean) {
			if (!this.attachmentGallery) {
				return;
			}
			this.attachmentGallery.readonly = readonly;
			this.toggle(this.querySelector("#proofAttachmentGalleryInfoPanel"), !readonly);
		}

		public init(loadAttachmentsUri: string | null = null) {
			const gallerySlot = this.querySelector("[data-gallery]");
			if (!gallerySlot) {
				return;
			}
			gallerySlot.innerHTML = template;
			this.loadAttachmentsUri = loadAttachmentsUri;
			this.attachmentGallery = this.querySelector<AttachmentGallery>("#proofAttachmentGallery");
			this.attachmentGallery?.setEmptyMessage(messages["proofAttachmentGallery.empty"]);
			this.attachmentGallery?.setFileSelectorOptions({
				acceptString: ProofAttachmentGallery.PROOF_ATTACHMENT_ACCEPT_STRING,
				maxFiles: ProofAttachmentGallery.MAX_PROOF_ATTACHMENT_COUNT,
				maxFileSize: ProofAttachmentGallery.MAX_PROOF_ATTACHMENT_SIZE
			});
			this.updateVisibility();
		}

		public update(proofRequirement: ProofRequirement) {
			this.proofRequirement = proofRequirement;
			this.updateVisibility();
		}

		private updateVisibility() {
			const requiresProof = this.requiresProof();
			this.toggle(this, requiresProof);
		}

		public reset() {
			this.attachmentGallery?.reset();
		}

		public loadProofAttachments(params: object) {
			if (!this.attachmentGallery || !this.loadAttachmentsUri) {
				return;
			}

			this.attachmentGallery.reset();
			if (!params) {
				return;
			}

			api(this.loadAttachmentsUri)
				.get(params)
				.done((data: AttachmentDownloadUrlResponse[]) => {
					this.attachmentGallery.setExistingFiles(data);
				});
		}

		/**
		 * Returns form-ready name/value pairs for proof attachments (files to upload and attachment IDs to delete).
		 * Used when posting saveWorkingTimeRequest so the backend can perform deletes.
		 */
		public getSerializedArrayElements(): JQuerySerializeArrayElement[] {
			return this.attachmentGallery?.getSerializedArrayElements() ?? [];
		}

		public getFilesToUpload(): File[] {
			return this.attachmentGallery?.getFilesToUpload() ?? [];
		}

		public getAttachmentsToDelete(): ProofAttachmentGalleryAttachment[] {
			return this.attachmentGallery?.getAttachmentsToDelete()
				?? ProofAttachmentGallery.EMPTY_ATTACHMENTS;
		}

		private toggle(element: HTMLElement, showOrHide: boolean) {
			element.style.display = showOrHide ? '' : 'none';
		}

		private requiresProof() {
			return this.proofRequirement !== ProofRequirement.NOT_APPLICABLE;
		}
	}

	interface ProofAttachmentGalleryAttachment {
		attachmentId: string;
		metadata: AttachmentFileMetadata;
		url: string;
	}
